import { Component, TemplateRef, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FormService } from '../services/form.service';

@Component({
	selector: 'app-form',
	templateUrl: './form.component.html',
	styleUrl: './form.component.css',
	standalone: false
})
export class FormComponent {
	modalRef: BsModalRef;
	@ViewChild('confirmationDialog') private confirmationDialog: TemplateRef<any>;

	loading = false;
	invalidUrl = false;
	showValidation = false;

	form: UntypedFormGroup;
	unsubmittedForm = true;
	submissionStatus = '';

	constructor(
		private modalService: BsModalService,
		private formBuilder: UntypedFormBuilder,
		private formService: FormService,
		private route: ActivatedRoute
	) { }


	ngOnInit(): void {
		this.form = this.formBuilder.group({
			'unionStoreNumber': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'firstName': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'lastName': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'middleInitial': new UntypedFormControl({ value: '', disabled: false }, [Validators.maxLength(1)]),
			'streetAddress': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'apartmentNumber': new UntypedFormControl({ value: '', disabled: false }, []),
			'city': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'state': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'zip': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^\d{5}$/)]),
			'email': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
			'phone': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]),
			'commsOptOut': new UntypedFormControl({ value: 'No', disabled: false }, [Validators.required]),
			'sex': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'dob': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/)]),
			'ssn': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^\d{3}-\d{2}-\d{4}$/)]),
			'relationship': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'dateOfHire': new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.pattern(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(19|20)\d{2}$/)]),
			'jobClass': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'employmentType': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'previousLocalNo': new UntypedFormControl({ value: '', disabled: false }, []),
			'beneficiaryFirstName': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'beneficiaryLastName': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'beneficiaryRelationship': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'shareGranted': new UntypedFormControl({ value: 100, disabled: false }, [Validators.required, Validators.min(1), Validators.max(100)]),
			'beneficiaryPhone': new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
			'beneficiaryEmail': new UntypedFormControl({ value: '', disabled: false }, [Validators.email]),
			'beneficiaryTwoFirstName': new UntypedFormControl({ value: '', disabled: false }, []),
			'beneficiaryTwoLastName': new UntypedFormControl({ value: '', disabled: false }, []),
			'beneficiaryTwoRelationship': new UntypedFormControl({ value: '', disabled: false }, []),
			'shareGrantedTwo': new UntypedFormControl({ value: 0, disabled: true }, [Validators.required, Validators.min(0), Validators.max(99)]),
			'beneficiaryTwoPhone': new UntypedFormControl({ value: '', disabled: false }, []),
			'beneficiaryTwoEmail': new UntypedFormControl({ value: '', disabled: false }, [Validators.email])
		}, {
			validators: [
				this.checkTotalShare()
			]
		});

		this.route.queryParams.subscribe(params => {
			if (params['unionStoreNumber']) {
				this.form.controls['unionStoreNumber'].setValue(params['unionStoreNumber'], { emitEvent: true });
			} else {
				this.invalidUrl = true;
			}
		});
	}

	checkTotalShare() {
		return (group: UntypedFormGroup): { [key: string]: any } => {
			if (this.form) {
				const shareGranted = parseInt(this.form.getRawValue()['shareGranted']);

				if (shareGranted < 1 || shareGranted > 100) {
					group.controls['shareGranted'].setErrors({ 'totalShare': true });
					return {};
				} else {
					group.controls['shareGranted'].setErrors(null);
					return {};
				}
			} else {
				return {};
			}
		};
	}

	isValidDob() {
		const dob = new Date(this.form.getRawValue()['dob']);
		return !isNaN(dob.getFullYear()) && ((new Date().getTime() - dob.getTime()) > (14 * 365.25 * 24 * 60 * 60 * 1000)); // 14 years in the past
	}

	isValidDoh() {
		const doh = new Date(this.form.getRawValue()['dateOfHire']);
		return !isNaN(doh.getFullYear()) && ((doh.getTime() - new Date().getTime()) < (30 * 24 * 60 * 60 * 1000)); // 30 days in the future
	}

	hasTwoBeneficiaries() {
		return this.form && parseInt(this.form.getRawValue()['shareGrantedTwo']) > 0;
	}

	beneficiaryAmountChanged() {
		if (this.form) {
			const shareGranted = parseInt(this.form.getRawValue()['shareGranted']);
			if (shareGranted < 100) {
				this.form.controls['beneficiaryTwoFirstName'].setValidators([Validators.required]);
				this.form.controls['beneficiaryTwoLastName'].setValidators([Validators.required]);
				this.form.controls['beneficiaryTwoRelationship'].setValidators([Validators.required]);
				this.form.controls['beneficiaryTwoPhone'].setValidators([Validators.required, Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)]);
				this.form.controls['shareGrantedTwo'].setValue(100 - shareGranted, { emitEvent: true });
			} else {
				this.form.controls['beneficiaryTwoFirstName'].setValidators([]);
				this.form.controls['beneficiaryTwoLastName'].setValidators([]);
				this.form.controls['beneficiaryTwoRelationship'].setValidators([]);
				this.form.controls['beneficiaryTwoPhone'].setValidators([]);
				this.form.controls['shareGrantedTwo'].setValue(0, { emitEvent: true });
			}
			this.form.controls['beneficiaryTwoFirstName'].updateValueAndValidity();
			this.form.controls['beneficiaryTwoLastName'].updateValueAndValidity();
			this.form.controls['beneficiaryTwoRelationship'].updateValueAndValidity();
			this.form.controls['beneficiaryTwoPhone'].updateValueAndValidity();
		}
	}

	submit() {
		if (this.form.valid && this.isValidDob() && this.isValidDoh()) {
			this.openModal(this.confirmationDialog, 'md');
		} else {
			this.showValidation = true;
		}
	}

	openModal(template: TemplateRef<any>, size: string) {
		this.modalRef = this.modalService.show(
			template,
			{ class: 'modal-' + size, backdrop: 'static' }
		);
	}

	submitForm() {
		this.unsubmittedForm = false;
		this.submissionStatus = 'sending';
		const formRequest = this.form.getRawValue();

		formRequest['dob'] = new Date(formRequest['dob']);
		formRequest['dateOfHire'] = new Date(formRequest['dateOfHire']);

		this.formService.submit(formRequest).subscribe(
			(response) => {
				this.submissionStatus = 'success';
			},
			(error) => {
				this.unsubmittedForm = true;
				this.submissionStatus = 'error';
			}
		);
	}

	formatPhone(field: string, e: any) {
		if (!e.keyCode || e.keyCode !== 8) {
			const rawValue = e.target.value.replace(/\D/g, '');
			let value = '';

			if (rawValue.length < 4) {
				value = rawValue;
			} else if (rawValue.length < 6) {
				value = '(' + rawValue.substring(0, 3) + ') ' + rawValue.substring(3, rawValue.length);
			} else if (rawValue.length < 9) {
				value = '(' + rawValue.substring(0, 3) + ') ' + rawValue.substring(3, 6) + '-' + rawValue.substring(6, rawValue.length);
			} else {
				value = '(' + rawValue.substring(0, 3) + ') ' + rawValue.substring(3, 6) + '-' + rawValue.substring(6, 10);
			}

			this.form.controls[field].setValue(value, { emitEvent: true });
		}
	}

	formatSsn(e: any) {
		if (!e.keyCode || e.keyCode !== 8) {
			const rawValue = e.target.value.replace(/\D/g, '');
			let value = '';

			if (rawValue.length < 4) {
				value = rawValue;
			} else if (rawValue.length < 6) {
				value = rawValue.substring(0, 3) + '-' + rawValue.substring(3, rawValue.length);
			} else if (rawValue.length < 9) {
				value = rawValue.substring(0, 3) + '-' + rawValue.substring(3, 5) + '-' + rawValue.substring(5, rawValue.length);
			} else {
				value = rawValue.substring(0, 3) + '-' + rawValue.substring(3, 5) + '-' + rawValue.substring(5, 9);
			}

			this.form.controls['ssn'].setValue(value, { emitEvent: true });
		}
	}

	formatDate(dateField: string, e: any) {
		if (!e.keyCode || e.keyCode !== 8) {
			const rawValue = e.target.value.replace(/\D/g, '');
			let value = '';

			if (rawValue.length < 2) {
				value = rawValue;
			} else if (rawValue.length < 4) {
				value = rawValue.substring(0, 2) + '/' + rawValue.substring(2, rawValue.length);
			} else if (rawValue.length < 9) {
				value = rawValue.substring(0, 2) + '/' + rawValue.substring(2, 4) + '/' + rawValue.substring(4, rawValue.length);
			} else {
				value = rawValue.substring(0, 2) + '/' + rawValue.substring(2, 4) + '/' + rawValue.substring(4, 8);
			}

			this.form.controls[dateField].setValue(value, { emitEvent: true });
		}
	}

}
